import React, { useState, useEffect, useRef } from "react";
import "./Task.css";
import TaskList from "../../components/TaskList/TaskList"; // Import the TaskHistoryTable
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import API_URL from "../../apiConfig";
import UserProfileBox from "../userProfileBox/UserProfileBox"; // Adjust the import path
import { TeamMembersService } from "../../services/teamMembers/teamMembersService";
import { TaskService } from "../../services/task/taskService";
import { useToast } from "../../components/common/ToastManager/ToastContext";
import Loader from "../../components/common/Loader/Loader";
import { HelenService } from "../../services/helen/helenService";
import TopBar from "../../components/TopBar/TopBar";
import Tooltip from "../../components/Tooltip";
import { getUserId } from "../../utils/tokenUtils";

interface BrandCategory {
  id: number;
  category_title: string;
  category_details: string;
  filename: string | null;
}

const TaskModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  refreshChatHistories: () => void;
  selectedHelen: any;
  subscriptionId: string | null;
}> = ({
  isOpen,
  onClose,
  refreshChatHistories,
  selectedHelen,
  subscriptionId,
}) => {
  const [productTitle, setProductTitle] = useState("");
  const [productDetails, setProductDetails] = useState("");
  const [brandName, setBrandName] = useState<string>("");
  const [materials, setMaterials] = useState("");
  const [userId] = useState(getUserId());
  const [showErrors, setShowErrors] = useState<{
    title: string;
    details: string;
  }>({
    title: "",
    details: "",
  });
  const [brandCategories, setBrandCategories] = useState<BrandCategory[]>([]);
  const [brandNameCategory, setBrandNameCategory] = useState<string>("");
  const [userData, setUserData] = useState<any>(null);
  const [selectedCategoryFilename, setSelectedCategoryFilename] = useState<
    string | null
  >(null);
  const [defaultBrandFilename, setDefaultBrandFilename] = useState<
    string | null
  >(null);
  const { showToast } = useToast();

  const [submitting, setSubmitting] = useState(false); // 🚀 new state

  const validContentRegex = /^[\s\S]*$/;

  const fetchUserData = async () => {
    try {
      const response = await TeamMembersService.getUsers(userId);
      const result = response.data;

      if (result?.user) {
        setUserData(result);

        if (Array.isArray(result.brandCategoryVoices)) {
          setBrandCategories(result.brandCategoryVoices);

          if (result.brandCategoryVoices.length > 0) {
            setBrandName(result.brandCategoryVoices[0].category_title);
            setBrandNameCategory(result.brandCategoryVoices[0].category_title);
          }
        }

        if (
          Array.isArray(result.brandVoices) &&
          result.brandVoices.length > 0
        ) {
          setDefaultBrandFilename(result.brandVoices[0].brand_details || null);
        }
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    fetchUserData();
  }, []);

  const clearFields = () => {
    setProductTitle("");
    setProductDetails("");
    setMaterials("");
    setShowErrors({ title: "", details: "" });

    if (brandCategories.length > 0) {
      const firstCategory = brandCategories[0];
      setBrandName(firstCategory.category_title);
      setBrandNameCategory(firstCategory.category_title);
      setSelectedCategoryFilename(firstCategory.filename || null);
    } else {
      setBrandName("");
      setBrandNameCategory("");
      setSelectedCategoryFilename(null);
    }
  };

  const handleModalClose = () => {
    clearFields();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = { title: "", details: "" };

    if (!productTitle.trim()) {
      errors.title = "Product Title is required";
    } else if (!validContentRegex.test(productTitle)) {
      errors.title =
        "Product Title must include letters or numbers (not just symbols)";
    }

    if (!productDetails.trim()) {
      errors.details = "Product Details are required";
    } else if (!validContentRegex.test(productDetails)) {
      errors.details =
        "Product Details must include letters or numbers (not just symbols)";
    }

    setShowErrors(errors);

    if (errors.title || errors.details) return;

    setSubmitting(true); // 🚀 disable button

    try {
      const queueResponse = await TaskService.createSingleTask(
        subscriptionId,
        selectedHelen?.id,
        productTitle,
        productDetails,
        selectedCategoryFilename || defaultBrandFilename || "",
        materials || "",
        "single"
      );
      if (queueResponse?.status !== 201) {
        clearFields();
        onClose();
        setSubmitting(false);
        showToast("Failed to queue message:", "error");
        return;
      }
      clearFields();
      refreshChatHistories();
      onClose();
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="single-task-modal-overlay">
      <div className="modal-container" onClick={handleModalClose}>
        <div
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{ padding: "10px" }}
        >
          <div className="task-header">
            <h2>Add a Single Task</h2>
            <img
              src="/images/cross.svg"
              alt="Close"
              className="task-icon"
              onClick={handleModalClose}
            />
          </div>
          <hr className="divider" />
          <p className="product-details">Fill In Product Details</p>

          <form onSubmit={handleSubmit}>
            {/* Product Title */}
            <div className="label-row">
              <label className="label">
                Product Title<span style={{ color: "red" }}> *</span>
              </label>
              <div className="char-count">{productTitle.length}/150</div>
            </div>

            <input
              className={`task-input ${showErrors.title ? "error" : ""}`}
              type="text"
              placeholder="Enter Details"
              value={productTitle}
              maxLength={150}
              onChange={(e) => {
                setProductTitle(e.target.value);
                if (showErrors.title)
                  setShowErrors((prev) => ({ ...prev, title: "" }));
              }}
            />

            {showErrors.title && (
              <span className="product_from-error-message">
                {showErrors.title}
              </span>
            )}

            {/* Product Details */}
            <div className="label-row">
              <label className="label">
                Product Details<span style={{ color: "red" }}> *</span>
              </label>
              <div className="char-count">{productDetails.length}/1000</div>
            </div>

            <input
              className={`task-input ${showErrors.details ? "error" : ""}`}
              type="text"
              placeholder="Enter Details"
              value={productDetails}
              maxLength={1000}
              onChange={(e) => {
                setProductDetails(e.target.value);
                if (showErrors.details)
                  setShowErrors((prev) => ({ ...prev, details: "" }));
              }}
            />
            {showErrors.details && (
              <span className="product_from-error-message">
                {showErrors.details}
              </span>
            )}

            {/* Brand Voice */}
            <label className="label">Category</label>
            <select
              className="dropdown"
              value={brandNameCategory}
              onChange={(e) => {
                const selectedTitle = e.target.value;
                const selected = brandCategories.find(
                  (c) => c.category_title === selectedTitle
                );
                setBrandName(selected?.category_title || "");
                setBrandNameCategory(selected?.category_title || "");
                setSelectedCategoryFilename(selected?.filename || null);
              }}
            >
              <option value="">Select Category</option>
              {brandCategories.map((category) => (
                <option key={category.id} value={category.category_title}>
                  {category.category_title}
                </option>
              ))}
            </select>

            {/* Materials */}

            <div className="label-row">
              <label className="label">Materials</label>
              <div className="char-count">{materials.length}/500</div>
            </div>

            <input
              className="task-input"
              type="text"
              placeholder="Enter Details"
              value={materials}
              onChange={(e) => setMaterials(e.target.value)}
              maxLength={500}
            />
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={handleModalClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={submitting}
              >
                {submitting ? "Adding..." : "Add to Queue"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

type BulkTaskModalProps = {
  isOpen: boolean;
  onClose: () => void;
  refreshChatHistories: () => void;

  selectedHelen: any;
  subscriptionId: string | null;
};

const BulkTaskModal: React.FC<BulkTaskModalProps> = ({
  isOpen,
  onClose,
  refreshChatHistories,
  selectedHelen,
  subscriptionId,
}) => {
  const { showToast } = useToast();
  const [userId] = useState(getUserId() || "6");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const originalFileName = useRef<string>("BulkTaskResults.xlsx");
  const [userData, setUserData] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>(""); // <-- Added

  const [brandCategories, setBrandCategories] = useState<BrandCategory[]>([]);
  const [brandNameCategory, setBrandNameCategory] = useState<string>("");
  const [brandName, setBrandName] = useState<string>("");
  const [defaultBrandFilename, setDefaultBrandFilename] = useState<
    string | null
  >(null);
  const [selectedCategoryFilename, setSelectedCategoryFilename] = useState<
    string | null
  >(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await TeamMembersService.getUsers(userId);
        const result = response.data;

        if (result?.user) {
          setUserData(result);

          if (Array.isArray(result.brandCategoryVoices)) {
            setBrandCategories(result.brandCategoryVoices);
            if (result.brandCategoryVoices.length > 0) {
              setBrandName(result.brandCategoryVoices[0].category_title);
              setBrandNameCategory(
                result.brandCategoryVoices[0].category_title
              );
            }
          }

          if (
            Array.isArray(result.brandVoices) &&
            result.brandVoices.length > 0
          ) {
            setDefaultBrandFilename(result.brandVoices[0].filename || null);
          }
        }
      } catch (error) {
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const downloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/BulkTaskTemplate.xlsx";
    link.download = "BulkTaskTemplate.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    setErrorMessage("");

    if (!file) {
      setErrorMessage("Please upload a valid Excel file.");
      return;
    }

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type)) {
      setErrorMessage("Invalid file type. Please upload a valid Excel file.");
      return;
    }

    setIsLoading(true);

    try {
      const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      originalFileName.current = `${fileNameWithoutExt}_updated.xlsx`;

      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      // Read all rows from Excel
      const allRows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
      }) as string[][];

      if (allRows.length < 2) {
        setErrorMessage("Excel file must have at least 2 rows (headers and data).");
        return;
      }

      
      // Helper function to check if row has mandatory headers
      const hasMandatoryHeaders = (row: string[]) => {
        const hasProductName = row.some(h => /Product\s*Name/i.test(h));
        const hasProductDetails = row.some(h => /Product\s*Details/i.test(h));
        return hasProductName && hasProductDetails;
      };
      
      // Check row 1 (index 0) for headers
      const firstRowHeaders = allRows[0] as string[];
      const firstRowHasMandatory = hasMandatoryHeaders(firstRowHeaders);
      
      // Check row 2 (index 1) for headers if it exists
      const secondRowHeaders = allRows.length > 1 ? (allRows[1] as string[]) : [];
      const secondRowHasMandatory = allRows.length > 1 ? hasMandatoryHeaders(secondRowHeaders) : false;
      
      let headerRow: string[];
      let dataRows: string[][];

      // Determine which row is the header row - CHECK MANDATORY HEADERS FIRST
      if (firstRowHasMandatory) {
        headerRow = firstRowHeaders;
        dataRows = allRows.slice(1);
      } else if (secondRowHasMandatory) {
        headerRow = secondRowHeaders;
        dataRows = allRows.slice(2);
      } else {
        setErrorMessage(
          "Faulty Excel - No valid headers found. Please ensure headers include: Product Name and Product Details"
        );
        return;
      }

      // Validate that required headers exist
      const hasProductName = headerRow.some(h => /Product\s*Name/i.test(h));
      const hasProductDetails = headerRow.some(h => /Product\s*Details/i.test(h));
      const requiredFieldsFound = hasProductName && hasProductDetails;

      if (!requiredFieldsFound) {
        setErrorMessage(
          "Faulty Excel (missing required columns: Product Name and Product Details)."
        );
        return;
      }

      // Find the actual column indices for mapping
      const productNameIndex = headerRow.findIndex(h => /Product\s*Name/i.test(h));
      const productDetailsIndex = headerRow.findIndex(h => /Product\s*Details/i.test(h));
      const categoryIndex = headerRow.findIndex(h => /Category/i.test(h));
      const materialsIndex = headerRow.findIndex(h => /Materials/i.test(h));

      // Convert data rows to JSON format using column indices (not header names)
      const jsonData: Record<string, any>[] = [];
      dataRows.forEach((row) => {
        const obj: Record<string, any> = {
          productName: row[productNameIndex] || "",
          productDetails: row[productDetailsIndex] || "",
          category: categoryIndex >= 0 ? row[categoryIndex] || "" : "",
          materials: materialsIndex >= 0 ? row[materialsIndex] || "" : "",
        };
        jsonData.push(obj);
      });

      // Remove empty rows
      const nonEmptyRows = jsonData.filter(
        (row) =>
          Object.values(row).some(
            (val) => val && String(val).trim().length > 0
          )
      );

      if (nonEmptyRows.length === 0) {
        setErrorMessage("No data found in file.");
        return;
      }

      // Check for any row missing mandatory fields
      const invalidRows = nonEmptyRows.filter(
        (row) =>
          !row.productName?.toString().trim() ||
          !row.productDetails?.toString().trim()
      );

      if (invalidRows.length > 0) {
        setErrorMessage("No Valid rows found in the file.");
        return;
      }

      // Check if product count exceeds limit (120)
      const productCount = nonEmptyRows.length;
      if (productCount > 120) {
        setErrorMessage(
          `Too many products (${productCount}). Maximum allowed is 120 products per file.`
        );
        return;
      }

      // All rows valid, map bulk_children
      const bulk_children = nonEmptyRows.map((row) => ({
        product_title: row.productName?.toString().trim() || "",
        product_details: row.productDetails?.toString().trim() || "",
        brand:
          row.category?.toString().trim() ||
          selectedCategoryFilename ||
          defaultBrandFilename ||
          "",
        materials: row.materials?.toString().trim() || "",
      }));

      // Prepare payload and submit
      const payload = {
        user_subscriptions_id: subscriptionId ? Number(subscriptionId) : 0,
        assigned_bot_id: Number(selectedHelen?.id),
        title: fileNameWithoutExt,
        bulk_children,
      };

      await TaskService.createBulkTask(payload);
      showToast("Bulk tasks created successfully!", "success");
      refreshChatHistories();
      handleClose();
    } catch (error: any) {
      setErrorMessage(
        error.response?.data?.error || "Failed to create bulk tasks."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setErrorMessage(""); // Reset error message
    onClose(); // Call the original onClose
  };

  return (
    <div className="modal-container" onClick={handleClose}>
      <div
        className="modal-content-bulk-order"
        style={{
          backgroundColor: "white",
          padding: "1.5rem",
          borderRadius: "8px",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "90vh",
          overflowY: "auto",
          animation: "slideUp 0.3s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="bulk-task-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2>Upload Bulk Task File</h2>
          <img
            src="/images/cross.svg"
            alt="Close"
            className="task-icon"
            style={{ cursor: "pointer", width: "24px", height: "24px" }}
            onClick={handleClose}
          />
        </div>
        <hr className="divider" />
        <div className="upload-container">
          <div
            className="upload-area"
            style={{ display: "flex", alignItems: "center" }}
          >
            <img
              src="/images/AddFiles.svg"
              alt="Add Files"
              className="upload-icon"
            />
            <label
              htmlFor="bulk-upload"
              className="upload-label"
              style={{ cursor: "pointer", marginLeft: "0.5rem" }}
            >
              Drag and drop file here, or click add files
            </label>
            <input
              ref={fileInputRef}
              id="bulk-upload"
              type="file"
              accept=".xlsx, .xls"
              onChange={handleBulkUpload}
              style={{ display: "none" }}
            />
            <button
              className="upload-btn"
              onClick={() => fileInputRef.current?.click()}
              style={{ marginLeft: "1rem" }}
            >
              Upload Files
            </button>
            {/* Error message display */}
          </div>

          <hr className="divider-bulk-sidebar" />
          {errorMessage && (
            <p key={errorMessage} style={{ color: "red", marginTop: "1rem" }}>
              {errorMessage}
            </p>
          )}
          <p
            className="file-info"
            style={{ marginTop: "1rem", textAlign: "center" }}
          >
            *Fill your details in our Excel file template for more accurate data
            import
          </p>
          <button
            className="download-template"
            onClick={downloadTemplate}
            style={{ marginTop: "0.5rem" }}
          >
            Download Template
          </button>
        </div>
      </div>
      {isLoading && <Loader />}
    </div>
  );
};

export interface TaskItem {
  id: number;
  parentTaskId: number | null;
  taskNumber: string;
  userId: number;
  userSubscriptionsId: number;
  assignedBotId: number;
  productTitle: string;
  productDetails: string;
  brand?: string;
  materials?: string;
  response?: string;
  status: string;
  taskType: string;
  scheduledFor: string;
  executedAt?: string;
  createdAt: string;
  updatedAt: string;
  optimizely?: { productNumber?: string };
}

export interface TaskGroup {
  parentTask: TaskItem;
  children: TaskItem[];
}

const Task: React.FC<{ activeTab: string }> = ({ activeTab }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [taskHistory, setTaskHistory] = useState<TaskGroup[]>([]);
  const [isTaskLoading, setIsTaskLoading] = useState(true);
  const [isBotsLoading, setIsBotsLoading] = useState(true);
  const [completedCount, setCompletedCount] = useState(0);
  const [queueCount, setQueueCount] = useState(0);
  const [botsAssignedToUser, setBotsAssignedToUser] = useState<any>(null);
  const [selectedHelen, setSelectedHelen] = useState<any>(null);
  const [dailyInteractions, setDailyInteractions] = useState<string>("-");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>("");
  const [tooltipTarget, setTooltipTarget] = useState<DOMRect | null>(null);
  const [tooltipContent, setTooltipContent] = useState("");

  useEffect(() => {
    if (activeTab === "tasks") {
      fetchBotsAssignedToUser(); // just fetch and set state
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "tasks" && selectedHelen) {
      fetchTaskHistories();
    }
  }, [activeTab, selectedHelen]);

  useEffect(() => {
    if (botsAssignedToUser) {
      setSelectedHelen(botsAssignedToUser[0]);
    }
  }, [botsAssignedToUser]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterOptions = [
    { value: "", label: "All Task" },
    { value: "completed", label: "Completed" },
    { value: "onhold", label: "On hold" },
    { value: "pending", label: "Pending" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const handleFilterSelect = (value?: string) => {
    setSelectedFilter(value);
    setIsMenuOpen(false);
  };

  // Logic to close the menu when clicking anywhere outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const fetchBotsAssignedToUser = async () => {
    try {
      setIsBotsLoading(true);
      HelenService.getBotsAssignedToUser("active").then((resp) => {
        if (resp?.data?.userAssignedBots) {
          setBotsAssignedToUser(resp.data.userAssignedBots);
          setIsBotsLoading(false);
        }
      });
    } catch (error) {
      setIsBotsLoading(false);
    }
  };

  const fetchTaskHistories = async () => {
    if (selectedHelen === null) {
      return;
    }
    setIsTaskLoading(true);

    try {
      const response = await TaskService.getTasks(selectedHelen?.id);
      const tasksList: TaskItem[] = response.data.tasks;

      // 1. Separate parents and children
      const task = tasksList.filter((task) => task.parentTaskId === null);
      const childTasks = tasksList.filter((task) => task.parentTaskId !== null);

      // 2. Map parent task ID to its children
      const childrenMap: Record<number, TaskItem[]> = {};
      childTasks.forEach((child) => {
        if (!childrenMap[child.parentTaskId!])
          childrenMap[child.parentTaskId!] = [];
        childrenMap[child.parentTaskId!].push(child);
      });

      // 3. Combine parent and children into TaskGroup
      const taskGroups: TaskGroup[] = task.map((parent) => ({
        parentTask: parent,
        children: childrenMap[parent.id] || [], // empty if no children
      }));

      // Now `taskGroups` has all parent tasks with their child tasks
      setTaskHistory(taskGroups);
      const usageLimit = selectedHelen?.usage_limit ?? 0;
      let creditUsed = 0;

      creditUsed = response.data.scheduledToday ?? 0;

      setDailyInteractions(`${creditUsed}/${usageLimit}`);
      setCompletedCount(response.data.completedTasks);
      setQueueCount(response.data.queuedTasks);
    } catch (error) {
      setTaskHistory([]);
      setCompletedCount(0);
      setQueueCount(0);
    } finally {
      setIsTaskLoading(false);
    }
  };

  const filteredTaskHistory = taskHistory
    .map((group) => {
      // Filter children based on selected status
      const filteredChildren = group.children.filter((child) =>
        selectedFilter ? child.status === selectedFilter : true
      );

      // Include parent only if it matches the filter or has matching children
      const includeParent =
        !selectedFilter ||
        group.parentTask.status === selectedFilter ||
        filteredChildren.length > 0;

      return includeParent
        ? { parentTask: group.parentTask, children: filteredChildren }
        : null;
    })
    .filter(Boolean) as TaskGroup[];

  if (activeTab !== "tasks") return null;

  const handleDownload = async (taskId: string) => {
    try {
      const res = await TaskService.exportTaskToExcel(taskId);

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tasks_${taskId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
    }
  };
  // SVG assets (defined as variables for cleaner rendering)
  const filterIconSvg = (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.80431 2.5C3.08423 2.5 2.5 3.09405 2.5 3.82534V4.60537C2.5 5.14706 2.70599 5.66798 3.07446 6.05981L7.11258 10.3536L7.11436 10.3509C7.89393 11.149 8.33254 12.2279 8.33254 13.3528V17.1626C8.33254 17.4172 8.59891 17.5798 8.82 17.4597L11.1196 16.2066C11.4668 16.017 11.6834 15.6486 11.6834 15.2487V13.3428C11.6834 12.2242 12.1158 11.1499 12.8883 10.3536L16.9264 6.05981C17.294 5.66798 17.5 5.14706 17.5 4.60537V3.82534C17.5 3.09405 16.9167 2.5 16.1966 2.5H3.80431Z"
        stroke="#3844D1"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const chevronSvg = (
    <svg
      className="chevron-icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d={isMenuOpen ? "M18 15L12 9L6 15" : "M6 9L12 15L18 9"}
        stroke="#3844D1"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (isBotsLoading)
    return (
      <main
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader inline={true} />
      </main>
    );

  if (!isBotsLoading && botsAssignedToUser?.length === 0) {
    return (
      <main
        className="content"
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <TopBar />
        <div
          style={{
            flexGrow: 1, // takes all remaining vertical space
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              padding: "1rem",
            }}
          >
            <p className="Notasks">No Bots Assigned</p>
            <p className="Addnewtaskstext">
              Your account doesn’t have any bots assigned at the moment.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="content">
      <TopBar />
      <section className="task-section">
        <h2 className="all-tasks-title">All Tasks</h2>
        <div className="task-tab">
          {(() => {
            return botsAssignedToUser?.map((helen: any, i: number) => {
              // Format tooltip with better spacing and labels
              const tooltipText = `Daily Interactions: ${helen.usage_limit}
              Plan: ${helen.plan_name}`;

              return (
                <button
                  key={i}
                  className={`tab-button ${
                    helen === selectedHelen ? "active-tab" : ""
                  }`}
                  onClick={() => setSelectedHelen(helen)}
                   onMouseEnter={(e) => {
                      setTooltipTarget(e.currentTarget.getBoundingClientRect());
                      setTooltipContent(tooltipText);
                    }}
                    onMouseLeave={() => {
                      setTooltipTarget(null);
                      setTooltipContent("");
                    }}
                >
          
                    <span className="bot-name">
                      {helen.helen_name.replace(/_/g, " ")}
                    </span>
                  
                </button>
              );
            });
          })()}
          <Tooltip targetRect={tooltipTarget} content={tooltipContent} />
        </div>
        <div className="task-stats">
          <div className="stat-box">
            <div className="stat-card">
              <img
                src="/images/CompletedTasks.svg"
                alt="Completed Tasks"
                className="stat-icon"
              />
              <div className="stat-text">
                <p className="stat-label">Completed Tasks</p>
                <span className="stat-value">{completedCount}</span>
              </div>
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-card">
              <img
                src="/images/TasksInqueue.svg"
                alt="Tasks in Queue"
                className="stat-icon"
              />
              <div className="stat-text">
                <p className="stat-label">Tasks in Queue</p>
                <span className="stat-value">{queueCount}</span>
              </div>
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-card">
              <img
                src="/images/CreditUsages.svg"
                alt="Daily Interactions"
                className="stat-icon"
              />
              <div className="stat-text">
                <p className="stat-label">Daily Interactions</p>
                <span className="stat-value">{dailyInteractions}</span>
              </div>
            </div>
          </div>
        </div>
        <section className="dashboard-main">
          <section className="tasks-list">
            {!isTaskLoading && taskHistory.length === 0 ? (
              <div className="task-container">
                <div className="centered-container">
                  <img
                    src="/images/Notasks.svg"
                    alt="No Tasks"
                    className="task-icon-main"
                  />
                  <p className="Notasks">No Tasks in Queue</p>
                  <p className="Addnewtaskstext">
                    Add a new task to get started!
                  </p>
                  <div className="task-buttons">
                    <button
                      className="task-btn bulk-task-btn"
                      onClick={() => setIsBulkModalOpen(true)}
                    >
                      + Add Bulk Task
                    </button>
                    <button
                      className="task-btn single-task-btn"
                      onClick={() => setIsModalOpen(true)}
                    >
                      + Add Single Task
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="task-top-bar">
                  <h2 className="task-title">Task List</h2>
                  <div className="task-actions">
                    <button
                      onClick={fetchTaskHistories}
                      className="refresh-button"
                      title="Refresh Data"
                      disabled={isTaskLoading}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10"/>
                        <polyline points="1 20 1 14 7 14"/>
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
                        <path d="M20.49 15A9 9 0 0 1 5.64 18.36L1 14"/>
                      </svg>
                    </button>

                    <div
                      className="custom-dropdown-container"
                      ref={dropdownRef}
                    >
                      <button
                        className="tasklist-filter-button"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-expanded={isMenuOpen}
                        aria-controls="filter-menu"
                        disabled={isTaskLoading}
                      >
                        {filterIconSvg}

                        <span>Filter by</span>
                        {chevronSvg}
                      </button>

                      {isMenuOpen && (
                        <div
                          className="filter-menu"
                          id="filter-menu"
                          role="menu"
                        >
                          {filterOptions.map((option) => (
                            <div
                              key={option.value}
                              className={`filter-menu-item ${
                                selectedFilter === option.value
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() => handleFilterSelect(option.value)}
                              role="menuitem"
                            >
                              {option.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      className="task-btn bulk-task-btn"
                      onClick={() => setIsBulkModalOpen(true)}
                      disabled={isTaskLoading}
                    >
                      + Add Bulk Task
                    </button>
                    <button
                      className="task-btn single-task-btn"
                      onClick={() => setIsModalOpen(true)}
                      disabled={isTaskLoading}
                    >
                      + Add Single Task
                    </button>
                  </div>
                </div>

                <TaskList
                  data={filteredTaskHistory}
                  handleDownload={handleDownload}
                  refreshChatHistories={fetchTaskHistories}
                  isLoading={isTaskLoading}
                />
              </>
            )}
          </section>
        </section>
      </section>
      <TaskModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        refreshChatHistories={fetchTaskHistories}
        selectedHelen={selectedHelen}
        subscriptionId={selectedHelen?.user_subscriptions_id}
      />
      <BulkTaskModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        refreshChatHistories={fetchTaskHistories}
        selectedHelen={selectedHelen}
        subscriptionId={selectedHelen?.user_subscriptions_id}
      />
    </main>
  );
};

export default Task;
